import { useState } from "react";
import { motion } from "framer-motion";
import { Camera, Sparkles, Search, Star, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6 },
  }),
};

const features = [
  {
    icon: Camera,
    title: "Snap a Product",
    desc: "See a product while you're out shopping? Take a quick photo and the app will identify it instantly.",
  },
  {
    icon: Search,
    title: "Ingredient Breakdown",
    desc: "Get a clear breakdown of what's in the product and whether it's right for your curl type.",
  },
  {
    icon: Star,
    title: "Personalised Rating",
    desc: "Based on your hair type and goals, get a personalised rating so you know if it's worth buying.",
  },
  {
    icon: Sparkles,
    title: "Build Your Routine",
    desc: "Save the products that work for you and build a complete curly hair care routine over time.",
  },
];

export default function CurlScanPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/curlscan/waitlist`, { email });
      toast.success("You're on the list! We'll be in touch.");
      setJoined(true);
      setEmail("");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("You're already on the waitlist!");
        setJoined(true);
      } else {
        toast.error("Something went wrong, please try again");
      }
    }
    setLoading(false);
  };

  return (
    <div className="pt-28 lg:pt-44" data-testid="curlscan-page">
      {/* Hero */}
      <section className="py-10 md:py-14 bg-warmcream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={0}
            >
              <p className="font-accent text-xl text-sunset mb-2">coming soon</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6">
                Justjjax: The Curl Edit
              </h1>
              <p className="text-charcoal/70 text-lg leading-relaxed mb-4">
                The app that helps you find the perfect products for your curls.
                Snap a photo of any hair product while you're out and about and
                instantly find out if it's right for your hair type.
              </p>
              <p className="text-charcoal/50 leading-relaxed mb-8">
                No more guessing in the hair aisle. No more wasting money on products
                that don't work. Just scan, learn and love your curls.
              </p>

              {!joined ? (
                <form onSubmit={handleWaitlist} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    className="flex-1 px-5 py-3.5 rounded-full border border-ocean/10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sunset/30"
                    data-testid="curlscan-email-input"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-olive text-white hover:bg-amber rounded-full px-8 py-3.5 text-sm font-medium transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
                    data-testid="curlscan-waitlist-btn"
                  >
                    {loading ? "Joining..." : "Join the Waitlist"}
                  </button>
                </form>
              ) : (
                <div className="bg-sage/10 rounded-2xl p-5 max-w-md mx-auto" data-testid="curlscan-joined">
                  <p className="font-medium text-sage">You're on the list!</p>
                  <p className="text-sm text-charcoal/60 mt-1">We'll let you know as soon as The Curl Edit launches.</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-10 md:py-14" data-testid="curlscan-features">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mb-16"
          >
            <motion.p variants={fadeUp} custom={0} className="font-accent text-xl text-sunset mb-2">
              how it works
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-display text-3xl sm:text-4xl tracking-tight">
              Your Curls, Your Rules
            </motion.h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="bg-warmcream rounded-3xl p-8 border border-ocean/5"
                data-testid={`curlscan-feature-${i}`}
              >
                <div className="w-12 h-12 bg-sunset/10 rounded-2xl flex items-center justify-center mb-5">
                  <feature.icon className="text-sunset" size={22} />
                </div>
                <h3 className="font-display text-lg mb-2">{feature.title}</h3>
                <p className="text-charcoal/60 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 bg-peachlight">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Sparkles className="text-sunset mx-auto mb-4" size={28} />
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-4">
            Stop Guessing. Start Scanning.
          </h2>
          <p className="text-charcoal/60 max-w-lg mx-auto mb-8">
            Justjjax: The Curl Edit is currently in development. Join the waitlist to be the first
            to try it when it launches.
          </p>
          {!joined ? (
            <form onSubmit={handleWaitlist} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 px-5 py-3.5 rounded-full border border-ocean/10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sunset/30"
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-olive text-white hover:bg-amber rounded-full px-8 py-3.5 text-sm font-medium transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
              >
                {loading ? "Joining..." : "Join the Waitlist"}
              </button>
            </form>
          ) : (
            <div className="bg-sage/10 rounded-2xl p-5 max-w-md mx-auto">
              <p className="font-medium text-sage">You're on the list!</p>
              <p className="text-sm text-charcoal/60 mt-1">We'll let you know as soon as The Curl Edit launches.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
